import React, { useCallback, useContext } from 'react'
import { AuthContext } from '../../context/auth/auth-context'
import axios from 'axios'
import { type AlertColor, IconButton, Tooltip } from '@mui/material'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import { formAlertErrorMessage, formAlertResponseMessageNoPrompt, isEmptyString } from '../../utils/string-utils'
import { formStrategyApiUrl } from '../../utils/api/servers'

export interface TradingToggleButtonProps {
  strategyName: string
  isTradingPaused: boolean
  displayToastWithMessage: (message: string, severity: AlertColor) => void
  controlsEnabled: boolean
}

const TradingToggleButton: React.FC<TradingToggleButtonProps> = (props) => {
  const { strategyName, isTradingPaused, displayToastWithMessage, controlsEnabled } = props
  const commandUrl = formStrategyApiUrl(strategyName, 'strategy-management/executeCommand')
  const isDisabled = isEmptyString(commandUrl) || !controlsEnabled

  const { accessToken } = useContext(AuthContext)

  const toggleTradingStatus = useCallback(() => {
    if (commandUrl == null || commandUrl.length === 0) {
      console.debug('[Status] Failed to toggle trading status (reason: invalid url)')
      displayToastWithMessage('The POST url is invalid - please check the related configuration again', 'warning')
      return
    }

    if (isEmptyString(strategyName)) {
      console.debug('[Status] No strategy data found - skipping action')
      displayToastWithMessage('No strategy data found - skipping action', 'warning')
      return
    }

    const message = {
      timestamp: new Date().toISOString(),
      strategyId: strategyName,
      command: isTradingPaused ? 'resumeTrading' : 'pauseTrading',
    }

    axios
      .post(commandUrl, message, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(response => {
        console.debug(response)
        displayToastWithMessage(formAlertResponseMessageNoPrompt(response), 'success')
      })
      .catch(error => {
        console.error(error)
        displayToastWithMessage(formAlertErrorMessage(error), 'error')
      })
  }, [strategyName, accessToken, isTradingPaused])

  return (
    <Tooltip title={isTradingPaused ? 'Resume Trading' : 'Pause Trading'}>
      <span>
        <IconButton size='small' disabled={isDisabled} onClick={toggleTradingStatus}>
          {
            isTradingPaused
              ? <PlayCircleOutlineIcon color={isDisabled ? 'inherit' : 'success'}/>
              : <PauseCircleOutlineIcon color={isDisabled ? 'inherit' : 'error'}/>
          }
        </IconButton>
      </span>
    </Tooltip>
  )
}

export default React.memo(TradingToggleButton)
